import { useState, useEffect, useMemo, useRef } from 'react'
import { RouterProvider } from 'react-router-dom'
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil'
import BigNumber from 'bignumber.js'
import { windowWidthState } from '@/recoil/Device'
import {
  systemStatusState,
  networkState,
  isSystemRunningValue
} from '@/recoil/Network'
import {
  marketSymbolState,
  marketMapState,
  marketListState,
  futuresMarketInfoMapState,
  marketIsReadyValue
} from '@/recoil/Market'
import { accountInfoState } from '@/recoil/AccountInfo'
import { allOpenOrderListState } from '@/recoil/OrderList'
import { tokenListState, accountWalletState } from '@/recoil/Wallet'
import { positionInfoState } from '@/recoil/Position'
import { dataAdapterState } from '@/recoil/DataAdapter'
import { popupNotificationListState } from '@/recoil/Notification'
import { API_ENDPOINT_MAP } from '@/constants/config'
import {
  EVENT_PING,
  EVENT_PONG,
  EVENT_REQUEST,
  EVENT_AUTH
} from '@/constants/webSocket'
import fetcher from '@/api/fetcher'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import usePreviousValue from '@/hooks/usePreviousValue'
import {
  request,
  // subscribe,
  subscribeTopics,
  unSubscribeTopics,
  pong,
  auth
} from '@/utils/webSocket'
import router from '@/utils/router'
import throttle from '@/utils/throttle'
import debounce from '@/utils/debounce'

import './App.css'

const REFRESH_TIMER = 60000

function isFuturesMarket(symbol) {
  return /^(PERP)/.test(symbol)
}

function marketListLoader() {
  return fetcher
    .getMarketList()
    .then(markets => {
      const marketMap = {}
      const marketList = []

      markets.forEach(market => {
        const [type, baseCurrency, quoteCurrency] = market.symbol.split('_')
        market.type = type.toLowerCase()
        market.isFutures = isFuturesMarket(market.symbol)
        market.baseCurrency = baseCurrency
        market.quoteCurrency = quoteCurrency
        market.priceDecimal = BigNumber(market.quote_tick).dp()
        market.sizeDecimal = BigNumber(market.base_tick).dp()
        market.minQuoteTick = market.quote_tick
        marketMap[market.symbol] = market
        if (baseCurrency !== 'WOO') {
          marketList.push(market.symbol)
        }
      })

      return {
        marketMap,
        marketList
      }
    })
    .catch(() => ({}))
}

function App() {
  const { accountId, walletReady, isInsufficientBalance } = useWalletSelector()
  const [network] = useRecoilState(networkState)
  const [marketSymbol] = useRecoilState(marketSymbolState)
  const setWindowWidth = useSetRecoilState(windowWidthState)
  const setSystemStatus = useSetRecoilState(systemStatusState)
  const setMarketMap = useSetRecoilState(marketMapState)
  const setMarketList = useSetRecoilState(marketListState)
  const setTokenList = useSetRecoilState(tokenListState)
  const setFuturesMarketInfoMap = useSetRecoilState(futuresMarketInfoMapState)
  const setAccountInfo = useSetRecoilState(accountInfoState)
  const setAccountWallet = useSetRecoilState(accountWalletState)
  const setAllOpenOrderList = useSetRecoilState(allOpenOrderListState)
  const setPositionInfo = useSetRecoilState(positionInfoState)
  const setPopupNotification = useSetRecoilState(popupNotificationListState)
  // const setFilledOrderList = useSetRecoilState(filledOrderListState)

  const isSystemRunning = useRecoilValue(isSystemRunningValue)
  const marketIsReady = useRecoilValue(marketIsReadyValue)
  const setDataAdapter = useSetRecoilState(dataAdapterState)

  const prevMarketSymbol = usePreviousValue(marketSymbol)

  const [systemStatusCheckerTrigger, setSystemStatusCheckerTrigger] =
    useState(false)
  const [refetchMarketInfoTrigger, setRefetchMarketInfoTrigger] =
    useState(false)
  const [reconnectTrigger, setReconnectTrigger] = useState(false)
  const [wsIsReady, setWsIsReady] = useState(false)
  const [privateWsReconnectTrigger, setPrivateWsReconnectTrigger] =
    useState(false)
  const [privateWsIsReady, setPrivateWsIsReady] = useState(false)

  const ws = useRef(null)
  const privateWs = useRef(null)
  const orderBookTs = useRef(null)

  const debounceUpdateOpenOrderList = useMemo(
    () =>
      debounce(
        () =>
          fetcher.getPendingOrders().then(rows => setAllOpenOrderList(rows)),
        100
      ),
    [setAllOpenOrderList]
  )

  // const debounceUpdateFilldOrderList = useMemo(
  //   () =>
  //     debounce(() => fetcher
  //       .getOrders({
  //         symbol: marketSymbol,
  //         status: 'FILLED',
  //         size: 100
  //       })
  //       .then(({ rows }) => setFilledOrderList(rows))
  //     , 100),
  //   [marketSymbol, setFilledOrderList]
  // )
  useEffect(() => {
    const throttleSetWindowWidth = throttle(() =>
      setWindowWidth(window.innerWidth)
    )
    const debounceSetWindowWidth = debounce(
      () => setWindowWidth(window.innerWidth),
      1000
    )
    window.addEventListener('resize', throttleSetWindowWidth)
    window.addEventListener('resize', debounceSetWindowWidth)
    return () => {
      window.removeEventListener('resize', throttleSetWindowWidth)
      window.removeEventListener('resize', debounceSetWindowWidth)
    }
  }, [setWindowWidth])

  useEffect(() => {
    let timer = setTimeout(
      () => setSystemStatusCheckerTrigger(v => !v),
      REFRESH_TIMER
    )

    fetcher
      .getSystemStatus()
      .then(({ status }) => setSystemStatus(status))
      .catch(() => setSystemStatus(-1))

    return () => clearTimeout(timer)
  }, [systemStatusCheckerTrigger, setSystemStatus])

  useEffect(() => {
    let timer = setTimeout(
      () => setRefetchMarketInfoTrigger(v => !v),
      REFRESH_TIMER
    )
    fetcher.getFuturesMarketInfoList().then(marketInfoList => {
      const map = {}
      marketInfoList.forEach(marketInfo => {
        map[marketInfo.symbol] = marketInfo
      })
      setFuturesMarketInfoMap(map)
    })

    return () => clearTimeout(timer)
  }, [refetchMarketInfoTrigger, setFuturesMarketInfoMap])

  useEffect(() => {
    isSystemRunning &&
      marketListLoader().then(({ marketMap, marketList }) => {
        if (!marketMap) {
          return
        }
        setMarketMap(marketMap)
        setMarketList(marketList)
      })
  }, [isSystemRunning, setMarketMap, setMarketList])

  useEffect(() => {
    isSystemRunning &&
      fetcher
        .getTokenList()
        .then(tokenList =>
          setTokenList(
            tokenList
              .filter(({ token }) => token !== 'WOO')
              .map(({ token }) => token)
          )
        )
  }, [isSystemRunning, setTokenList])

  useEffect(() => {
    marketIsReady && fetcher.setNetwork(network)
  }, [marketIsReady, network])

  useEffect(() => {
    if (!marketIsReady || !walletReady) {
      return
    }
    fetcher.getAccountInfo().then(setAccountInfo)
  }, [marketIsReady, walletReady, setAccountInfo])

  useEffect(() => {
    if (!marketIsReady || !walletReady) {
      return
    }
    fetcher
      .getAccountHolding()
      .then(tokenBalanceData => setAccountWallet({ tokenBalanceData }))
  }, [marketIsReady, walletReady, setAccountWallet])

  useEffect(() => {
    if (!marketIsReady || !walletReady) {
      return
    }
    fetcher.getPendingOrders().then(rows => setAllOpenOrderList(rows))
  }, [marketIsReady, walletReady, setAllOpenOrderList])

  useEffect(() => {
    if (!marketIsReady || !walletReady) {
      return
    }
    fetcher.getPositions().then(setPositionInfo)
  }, [marketIsReady, walletReady, setPositionInfo])

  useEffect(() => {
    if (!marketIsReady) {
      return
    }
    // console.log('initial')
    const accountIdParams =
      accountId || 'OqdphuyCtYWxwzhxyLLjOWNdFP7sQt8RPWzmb5xY'
    const endpoint = `${API_ENDPOINT_MAP[network].public}/${accountIdParams}`
    ws.current = new WebSocket(endpoint)
    ws.current.onopen = () => {
      setWsIsReady(true)
      subscribeTopics(ws.current, [
        { topic: 'tickers' },
        { topic: 'indexprices' },
        { topic: 'markprices' },
        { topic: 'openinterests' }
      ])
    }

    ws.current.onmessage = ({ data: message }) => {
      const { id, event, topic, data, ts } = JSON.parse(message)
      if (event === EVENT_PING) {
        return pong(ws.current)
      } else if (!(topic || [EVENT_REQUEST, EVENT_PONG].includes(event))) {
        return
      }

      if (event === EVENT_REQUEST && id.includes('@orderbook')) {
        orderBookTs.current = data.ts
      } else if (topic && topic.includes('@orderbookupdate')) {
        if (orderBookTs.current !== data.prevTs) {
          return request(ws.current, {
            id: `${data.symbol}@orderbook`,
            params: {
              type: 'orderbook',
              symbol: data.symbol
            }
          })
        }
        orderBookTs.current = ts
      }

      setDataAdapter({ id, event, topic, data, ts })
    }

    ws.current.onclose = event => {
      // console.log('onclose', new Date(), event)
      setTimeout(() => setReconnectTrigger(!reconnectTrigger), 3000)
    }

    return () => {
      // console.log('unmount')
      setWsIsReady(false)
      ws.current.onclose = () => {}
      ws.current.close(1000)
    }
  }, [marketIsReady, network, accountId, reconnectTrigger, setDataAdapter])

  useEffect(() => {
    if (!marketIsReady || !walletReady) {
      return
    }
    // console.log('initial private ws')
    const endpoint = `${API_ENDPOINT_MAP[network].private}/${accountId}`
    privateWs.current = new WebSocket(endpoint)
    privateWs.current.onopen = () => {
      setPrivateWsIsReady(true)
      auth(privateWs.current)
    }

    privateWs.current.onmessage = ({ data: message }) => {
      const { id, event, success, topic, data, ts } = JSON.parse(message)
      if (event === EVENT_PING) {
        return pong(privateWs.current)
      } else if (event === EVENT_AUTH && success) {
        subscribeTopics(privateWs.current, [
          { topic: 'account' },
          { topic: 'notifications' },
          { topic: 'executionreport' },
          { topic: 'balance' },
          { topic: 'position' }
        ])
      } else if (!(topic || [EVENT_REQUEST, EVENT_PONG].includes(event))) {
        return
      }

      if (topic === 'executionreport') {
        debounceUpdateOpenOrderList(true)
      }

      setDataAdapter({ id, event, topic, data, ts })
    }

    privateWs.current.onclose = event => {
      // console.log('private ws onclose', new Date(), event)
      setTimeout(
        () => setPrivateWsReconnectTrigger(!privateWsReconnectTrigger),
        3000
      )
    }

    return () => {
      // console.log('unmount')
      setPrivateWsIsReady(false)
      privateWs.current.onclose = () => {}
      privateWs.current.close(1000)
    }
  }, [
    marketIsReady,
    walletReady,
    network,
    accountId,
    setDataAdapter,
    debounceUpdateOpenOrderList,
    privateWsReconnectTrigger
  ])

  useEffect(() => {
    if (!wsIsReady || !marketSymbol) {
      return
    }

    if (prevMarketSymbol !== marketSymbol) {
      unSubscribeTopics(ws.current, [
        { topic: `${prevMarketSymbol}@trade` },
        { topic: `${prevMarketSymbol}@orderbookupdate` }
      ])

      isFuturesMarket(prevMarketSymbol) &&
        unSubscribeTopics(ws.current, [
          { topic: `${prevMarketSymbol}@estfundingrate` }
        ])
    }

    request(ws.current, {
      id: `${marketSymbol}@trade`,
      params: {
        type: 'trade',
        symbol: marketSymbol,
        limit: 50
      }
    })
    request(ws.current, {
      id: `${marketSymbol}@orderbook`,
      params: {
        type: 'orderbook',
        symbol: marketSymbol
      }
    })

    subscribeTopics(ws.current, [
      { topic: `${marketSymbol}@trade` },
      { topic: `${marketSymbol}@orderbookupdate` }
    ])

    isFuturesMarket(marketSymbol) &&
      subscribeTopics(ws.current, [{ topic: `${marketSymbol}@estfundingrate` }])
  }, [wsIsReady, prevMarketSymbol, marketSymbol])

  useEffect(() => {
    isInsufficientBalance &&
      setPopupNotification({
        notification: {
          key: Date.now(),
          status: 'error',
          message: 'Insufficient NEAR balance to pay transaction fee'
        }
      })
  }, [isInsufficientBalance, setPopupNotification])

  return (
    <div className="app">
      <RouterProvider router={router} />
    </div>
  )
}

export default App
